import React, { memo, useEffect, useRef, useState } from "react";
import { PAYMENT_SUCCESS_BANNER } from "@swiggy-private/payment-test-ids";
import { CounterAnimation, DigitContainer, PunctuationContainer, ScrollingNumber, TickerText } from "./styles";
import { BILL_ELEMENT_ENTRIES, currencyFormat } from "@swiggy-private/dineout-payment-success-ui";
import { getStartAmount } from "../../../helpers";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const ANIMATION_DURATION = 150;
const ANIMATION_DELAY = 500;
const DIGIT_DURATION = 1000;

export interface TickerData {
    targetNumber: number;
    originalAmount: number;
    showTickerAnimation: boolean;
    isRewardRedeemed: boolean;
    redeemableRewards?: number | undefined;
    tickerTextColor?: keyof IColors;
}

const PaymentSuccessTicker: React.FC<TickerData> = ({
    originalAmount,
    targetNumber,
    showTickerAnimation,
    isRewardRedeemed,
    tickerTextColor,
}) => {
    const [previousDigits, setPreviousDigits] = useState<string[]>([]);
    const [currentDigits, setCurrentDigits] = useState<string[]>([]);
    const previousTargetAmount = useRef<number>(targetNumber);
    const finalTargetAmount = targetNumber;

    const startAmount = getStartAmount(
        isRewardRedeemed,
        originalAmount,
        showTickerAnimation,
        previousTargetAmount.current,
    );

    const defaultCurrentValue = showTickerAnimation || isRewardRedeemed ? startAmount : finalTargetAmount;
    const [currentValue, setCurrentValue] = useState(defaultCurrentValue);
    const [animationInProgress, setIsAnimationInProgress] = useState(false);
    const containerRefs = useRef<(HTMLDivElement | null)[]>([]);
    const animationFrameIdRef = useRef<number | null>(null);
    const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (startAmount === finalTargetAmount || (!showTickerAnimation && !isRewardRedeemed)) {
            setCurrentValue(finalTargetAmount);
            return;
        }

        // Animation should start if showTickerAnimation is true or isRewardRedeemed is true
        setCurrentValue(startAmount);

        // Clear any existing timeout
        if (animationTimeoutRef.current) {
            clearTimeout(animationTimeoutRef.current);
        }

        animationTimeoutRef.current = setTimeout(() => {
            setIsAnimationInProgress(true);
            const start = Date.now();
            const step = () => {
                const now = Date.now();
                const progress = Math.min((now - start) / ANIMATION_DURATION, 1);
                const next = Math.round(startAmount + progress * (finalTargetAmount - startAmount));
                setCurrentValue(next);

                if (progress < 1) {
                    animationFrameIdRef.current = requestAnimationFrame(step);
                }
            };

            animationFrameIdRef.current = requestAnimationFrame(step);

            return () => {
                if (animationFrameIdRef.current !== null) {
                    cancelAnimationFrame(animationFrameIdRef.current);
                }
            };
        }, ANIMATION_DELAY);

        return () => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [startAmount, finalTargetAmount, showTickerAnimation, isRewardRedeemed]);

    useEffect(() => {
        // Skip formatting if animation is disabled
        if (!showTickerAnimation && !isRewardRedeemed) {
            // Just set directly to the target value format with no animation
            const finalDigits = currencyFormat(finalTargetAmount).split("");
            setPreviousDigits(finalDigits);
            setCurrentDigits(finalDigits);
            return;
        }

        // Format the start amount and current value into currency strings
        const _previousDigits = currencyFormat(startAmount).split("");
        const _currentDigits = currencyFormat(currentValue).split("");

        // Make both arrays the same length by padding the shorter one with spaces at the beginning
        while (_previousDigits.length < _currentDigits.length) _previousDigits.unshift(" ");
        while (_currentDigits.length < _previousDigits.length) _currentDigits.unshift(" ");

        // Remove all the extra spaces we added earlier
        const trimmedOriginalDigits = _previousDigits.filter(digit => digit !== " ");
        const trimmedFinalDigits = _currentDigits.filter(digit => digit !== " ");

        setPreviousDigits(trimmedOriginalDigits);
        setCurrentDigits(trimmedFinalDigits);
    }, [currentValue, startAmount, finalTargetAmount, showTickerAnimation, isRewardRedeemed]);

    useEffect(() => {
        // Skip animation if animation is disabled
        if (!showTickerAnimation && !isRewardRedeemed) {
            return;
        }

        // Loop through each digit in the currentDigits array
        currentDigits.forEach((digit, idx) => {
            // Get the corresponding digit from the previousDigits array
            const fromDigit = previousDigits[idx];

            // Get the reference to the DOM element for this digit position
            const ref = containerRefs.current[idx];

            // Check if the digit has changed and is not a space (used for padding)
            const isChanging = fromDigit !== digit && digit !== " ";

            // Get the index of the current digit in the DIGITS array (0-9)
            // This tells us how far to scroll vertically for the animation
            const position = DIGITS.indexOf(digit);

            // If we have a valid reference, the digit has changed, and it's a number,
            // then apply a CSS transform to animate the scroll to the new digit
            if (ref && isChanging && position >= 0 && animationInProgress) {
                ref.style.transition = `transform ${DIGIT_DURATION}ms cubic-bezier(0.16, 1, 0.3, 1)`; // smooth animation
                ref.style.transform = `translateY(-${position * 60}px)`; // scroll to the correct digit vertically
            }
        });
    }, [currentDigits, previousDigits, showTickerAnimation, animationInProgress, isRewardRedeemed]);

    return (
        <CounterAnimation>
            <TickerText
                variant="V2.subheading1.neutral.medium"
                data-testid={PAYMENT_SUCCESS_BANNER.SAVING_DIGIT}
                isRewardRedeemed={isRewardRedeemed}
                tickerTextColor={tickerTextColor}
            >
                {BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL}
            </TickerText>

            {currentDigits.map((char, idx) => {
                const isChanging = previousDigits[idx] !== char;
                const isNumeric = !isNaN(parseInt(char));
                const isPunct = char === "," || char === ".";

                if (isPunct || !isNumeric) {
                    return (
                        <PunctuationContainer key={idx}>
                            <TickerText
                                variant="V2.subheading1.neutral.bold"
                                color="positive_Sub"
                                isRewardRedeemed={isRewardRedeemed}
                                tickerTextColor={tickerTextColor}
                            >
                                {char}
                            </TickerText>
                        </PunctuationContainer>
                    );
                }

                return (
                    <DigitContainer key={idx}>
                        {isChanging && DIGITS.includes(char) ? (
                            <ScrollingNumber ref={el => (containerRefs.current[idx] = el)}>
                                {DIGITS.map(digit => (
                                    <TickerText
                                        key={digit}
                                        variant="V2.subheading1.neutral.bold"
                                        color="positive_Sub"
                                        isRewardRedeemed={isRewardRedeemed}
                                        tickerTextColor={tickerTextColor}
                                    >
                                        {digit}
                                    </TickerText>
                                ))}
                            </ScrollingNumber>
                        ) : (
                            <TickerText
                                variant="V2.subheading1.neutral.bold"
                                color="positive_Sub"
                                isRewardRedeemed={isRewardRedeemed}
                                tickerTextColor={tickerTextColor}
                            >
                                {char}
                            </TickerText>
                        )}
                    </DigitContainer>
                );
            })}
        </CounterAnimation>
    );
};

export default memo(PaymentSuccessTicker);
